import $ from 'jquery';
import { gsap } from 'gsap';

export function initSolutions() {
  const $section = $('.solutions');

  if (!$section.length) {
    return;
  }

  const $tabs = $section.find('.solutions__tab');
  const $dots = $section.find('.solutions__dot');
  const $media = $section.find('.solutions__media');
  const $description = $section.find('.solutions__description');
  const $track = $section.find('.solutions__tabs-track');
  const $container = $section.find('.solutions__tabs');

  const SLIDE_DURATION = 0.32;
  const DRAG_THRESHOLD = 5;
  const SWIPE_GAP = 16;
  const FADE_DURATION = 300;

  const isMobile = () => window.matchMedia('(max-width: 600px)').matches;

  const isVideoUrl = (src) => /\.(mp4|webm|ogg|mov|m4v)(\?|#|$)/i.test(src);

  function createMediaElement(src) {
    if (isVideoUrl(src)) {
      const video = document.createElement('video');
      video.className = 'solutions__image';
      video.src = src;
      video.autoplay = true;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');

      return video;
    }

    const img = document.createElement('img');
    img.className = 'solutions__image';
    img.src = src;

    return img;
  }

  $tabs.each(function () {
    const src = $(this).data('image');

    if (src && !isVideoUrl(src)) {
      const img = new Image();
      img.src = src;
    }
  });

  let currentX = 0;
  let isDragging = false;
  let hasDragged = false;
  let dragStartX = 0;
  let dragStartTrackX = 0;
  let prevIndex = 0;

  function clampTrack(x) {
    const containerWidth = $container.outerWidth();
    const trackWidth = $track[0].scrollWidth;

    if (trackWidth <= containerWidth) {
      return 0;
    }

    const maxNegX = containerWidth - trackWidth;

    return Math.max(maxNegX, Math.min(0, x));
  }

  function setTrack(x) {
    currentX = x;
    $track.css('transform', `translateX(${x}px)`);
    updateEdgeFades();
  }

  function updateEdgeFades() {
    const containerWidth = $container.outerWidth();
    const trackWidth = $track[0].scrollWidth;
    const maxNegX = trackWidth > containerWidth ? containerWidth - trackWidth : 0;
    const atStart = currentX >= -0.5;
    const atEnd = currentX <= maxNegX + 0.5;
    $container.toggleClass('is-at-start', atStart).toggleClass('is-at-end', atEnd);
  }

  function scrollTabIntoView(index) {
    const tab = $tabs.get(index);

    if (!tab) {
      return;
    }

    const containerWidth = $container.outerWidth();
    const tabCenter = tab.offsetLeft + tab.offsetWidth / 2;
    const desiredX = containerWidth / 2 - tabCenter;

    setTrack(clampTrack(desiredX));
  }

  let isSwapping = false;

  function swapImage(src, direction) {
    if (!src) {
      return;
    }

    if (!isMobile()) {
      const $current = $media.find('.solutions__image').first();

      if ($current.attr('src') === src) {
        return;
      }

      $current.css('opacity', 0);
      setTimeout(() => {
        const next = createMediaElement(src);

        $(next).css('opacity', 0);
        $media.empty();
        $media[0].appendChild(next);

        const showIn = () => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => $(next).css('opacity', 1));
          });
        };

        if (next.tagName === 'VIDEO') {
          next.addEventListener('loadeddata', showIn, { once: true });

          return;
        }

        if (next.complete && next.naturalWidth > 0) {
          showIn();

          return;
        }

        $(next).one('load', showIn);
      }, FADE_DURATION);

      return;
    }

    if (isSwapping) {
      return;
    }

    const $current = $media.find('.solutions__image').last();

    if ($current.attr('src') === src) {
      return;
    }

    isSwapping = true;
    const dir = direction === -1 ? -1 : 1;
    const newImg = createMediaElement(src);
    $media[0].appendChild(newImg);

    gsap.fromTo(
      newImg,
      { xPercent: dir === 1 ? 100 : -100, x: dir === 1 ? SWIPE_GAP : -SWIPE_GAP },
      { xPercent: 0, x: 0, duration: SLIDE_DURATION, ease: 'power3.out' }
    );
    gsap.to($current[0], {
      xPercent: dir === 1 ? -100 : 100,
      x: 0,
      opacity: 0,
      duration: SLIDE_DURATION,
      ease: 'power3.out',
      onComplete: () => {
        $current.remove();
        isSwapping = false;
      },
    });
  }

  function swapDescription(text) {
    if (text === undefined || text === null || !$description.length) {
      return;
    }

    gsap.to($description, {
      opacity: 0,
      duration: 0.15,
      onComplete: function () {
        $description.html(text);
        gsap.to($description, { opacity: 1, duration: 0.15 });
      },
    });
  }

  function getDirection(index) {
    if (index > prevIndex) {
      return 1;
    }

    if (index < prevIndex) {
      return -1;
    }

    return 0;
  }

  function activate(index) {
    const direction = getDirection(index);
    $tabs.removeClass('is-active').eq(index).addClass('is-active');
    $dots.removeClass('is-active').eq(index).addClass('is-active');

    swapImage($tabs.eq(index).data('image'), direction);
    swapDescription($tabs.eq(index).data('description'));
    scrollTabIntoView(index);
    prevIndex = index;
  }

  $tabs.on('click', function (e) {
    if (hasDragged) {
      e.preventDefault();
      e.stopImmediatePropagation();
      hasDragged = false;
      return;
    }
    activate($tabs.index(this));
  });

  $dots.on('click', function () {
    activate($dots.index(this));
  });

  $container.on('mousedown', function (e) {
    isDragging = true;
    hasDragged = false;
    dragStartX = e.clientX;
    dragStartTrackX = currentX;
    $track.css('transition', 'none');
    $container.addClass('is-dragging');
    e.preventDefault();
  });

  $(document).on('mousemove.solutions', function (e) {
    if (!isDragging) {
      return;
    }

    const delta = e.clientX - dragStartX;

    if (Math.abs(delta) > DRAG_THRESHOLD) {
      hasDragged = true;
    }

    setTrack(clampTrack(dragStartTrackX + delta));
  });

  $(document).on('mouseup.solutions blur.solutions', function () {
    if (!isDragging) {
      return;
    }

    isDragging = false;
    $track.css('transition', '');
    $container.removeClass('is-dragging');
  });

  $container.on('touchstart', function (e) {
    isDragging = true;
    hasDragged = false;
    dragStartX = e.originalEvent.touches[0].clientX;
    dragStartTrackX = currentX;
    $track.css('transition', 'none');
    $container.addClass('is-dragging');
  });

  $container.on('touchmove', function (e) {
    if (!isDragging) {
      return;
    }

    const delta = e.originalEvent.touches[0].clientX - dragStartX;

    if (Math.abs(delta) > DRAG_THRESHOLD) {
      hasDragged = true;
    }

    setTrack(clampTrack(dragStartTrackX + delta));
  });

  $container.on('touchend touchcancel', function () {
    if (!isDragging) {
      return;
    }

    isDragging = false;
    $track.css('transition', '');
    $container.removeClass('is-dragging');
  });

  const SWIPE_THRESHOLD = 50;

  let swipeStartX = 0;
  let swipeStartY = 0;
  let isSwipeActive = false;

  $media.on('touchstart', function (e) {
    const touch = e.originalEvent.touches[0];

    swipeStartX = touch.clientX;
    swipeStartY = touch.clientY;
    isSwipeActive = true;
  });

  $media.on('touchend', function (e) {
    if (!isSwipeActive) {
      return;
    }

    isSwipeActive = false;

    const touch = e.originalEvent.changedTouches[0];
    const dx = touch.clientX - swipeStartX;
    const dy = touch.clientY - swipeStartY;

    if (Math.abs(dx) <= Math.abs(dy) || Math.abs(dx) < SWIPE_THRESHOLD) {
      return;
    }

    const currentIndex = $tabs.index($tabs.filter('.is-active'));
    const nextIndex = currentIndex + (dx < 0 ? 1 : -1);

    if (nextIndex < 0 || nextIndex >= $tabs.length) {
      return;
    }

    activate(nextIndex);
  });

  $media.on('touchcancel', function () {
    isSwipeActive = false;
  });

  setTrack(0);
}
